import {ErrorHandler, ModuleWithProviders, NgModule} from "@angular/core";
import {FormsModule, ReactiveFormsModule} from "@angular/forms";
import {HTTP_INTERCEPTORS, HttpClientModule} from "@angular/common/http";
import {NgbModule} from "@ng-bootstrap/ng-bootstrap";
import {ApiModule as GbApiModule} from "./gb-api";
import {ApiModule as StammdatenApiModule} from "./stammdaten-api";
import {ApiModule as BaseApiModule} from "./base-api";
import {ApiModule as AvApiModule} from "./av-api";
import {ApiModule as AuftragApiModule} from "./auftrag-api";
import {ApiModule as GwrApiModule} from "./gwr-api";
import {ApiModule as PersonstammApiModule} from "./personstamm-api";
import {ApiModule as GrunbuchmeldungApiModule} from "./grundbuchmeldung-api";
import {ApiModule as AmtlichebewerungApiModule} from "./amtlichebewertung-api";
import {ApiModule as OerebApiModule} from "./oereb-api";
import {ApiModule as MapsApipModule} from "./maps-api";
import {ApiModule as BatchAuftragApiModule} from "./batch-auftrag-api";
import {apiConfigFactory} from './api.config';
import {RouterModule} from "@angular/router";
import {AppConfigService} from "./services/app-config.service";
import {LogModule} from "../log/log.module";
import {CapAlertListComponent} from "./extension/exceptions/cap-alert-list.component";
import {CapExceptionMapper} from "./extension/exceptions/cap-exception-mapper";
import {CapExceptionHandler} from "./extension/exceptions/cap-exception-handler";
import {GbPrintService} from "./services/app-print.service";
import {AppMenuService} from "./services/menu/app-menu.service";
import {AppCustomConfigService} from "./services/app-custom-config.service";
import {CommonModule, LocationStrategy, PathLocationStrategy} from "@angular/common";
import {FormatConfigService} from "./services/format-config.service";
import {AppFormatConfigService} from "./services/app-format-config.service";
import {CapNavItemComponent} from "./extension/navigation/cap-nav-item.component";
import {BreadcrumbService} from "./services/breadcrumb.service";
import {CookieService} from "ngx-cookie-service";
import {AppPermissionService} from "./services/app-permission.service";
import {UserPickerComponent} from "app/core/extension/user-picker/user-picker.component";
import {ExcludePipe} from "./extension/exclude.pipe";
import {Ng2CompleterModule} from "ng2-completer";
import {AppAuszugCookieService} from "./services/app-auszug-cookie-service";
import {TimepointService} from "./services/timepoint.service";
import {MenuTreeService} from "./services/menu/menu-tree-service";
import {StandardrechtViewmodelService} from "./services/standardrecht-viewmodel.service";
import {ClientSessionIdHttpInterceptor} from "./services/ClientSessionIdHttpInterceptor";
import {EigentumViewmodelService} from "./services/eigentum-viewmodel.service";
import {GrundpfandrechtViewmodelService} from "./services/grundpfandrecht-viewmodel.service";
import {CapiLoginService} from "./extension/login/capi-login-service";
import {
  ElasticSearchExceptionHandler,
  FremdsystemErrorExceptionHandler,
  FremdsystemNeskoErrorExceptionHandler,
  IcpPrintExceptionHandler,
  LogAndAlertExceptionHandler,
  MizenExceptionHandler,
  NoPermissionExceptionHandler,
  ResolverValidationExceptionHandler,
  SearchResultValidationExceptionHandler,
  TooManyRequestsExceptionHandler,
  UnauthorizedExceptionHandler
} from "./extension/exceptions/exception-handler-rule";
import {BodenbedeckungViewmodelService} from "./services/bodenbedeckung-viewmodel.service";
import {RouteHistoryService} from "./services/route-history.service";
import {SuchparameterService} from "./services/suchparameter.service";
import {RangregelungViewmodelService} from "./services/rangregelung-viewmodel.service";
import {HistoryModeHttpInterceptor} from "./services/HistoryModeHttpInterceptor";
import {TranslateModule} from '@ngx-translate/core';
import {OAuthModule} from 'angular-oauth2-oidc';
import {NavMenuComponent} from './extension/navigation/cap-nav-menu.component';
import {HeaderComponent} from './extension/header/cap-header.component';
import {CapAlertComponent} from './extension/exceptions/cap-alert.component';
import {CapActionbarComponent} from './extension/actionbar/actionbar.component';
import {CapAlertService} from "./extension/exceptions/cap-alert.service";
import {SearchResponseInterceptor} from './services/SearchResponseInterceptor';
import {AppGbgemeindeCompleterService} from "./services/app-gbgemeinde-completer.service";
import {ErrorHandlingHttpInterceptor} from './services/ErrorHandlingHttpInterceptor';

/**
 * The core module contributes declarations throughout the application.
 * The CoreModule.forRoot() contributes singleton-services throughout the application.
 *
 * It is available in two flavors:
 * CoreModule, which can be imported by any module in the application (almost Bedag-Shared Module),
 * and CoreModule.forRoot(), which must (only) be imported by the application module.
 *
 * The reason for this split is that angular uses different scoping rules for declarations and providers. Specifically,
 * declarations must be imported in every module, while providers can be inherited from parent injectors - and should,
 * since registering them again would create new independent instances. We therefore put our declarations in the
 * CoreModule, and our singleton providers into the CoreModuleForRoot.
 */
@NgModule({
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    TranslateModule,
    NgbModule
  ],
  declarations: [
    CapAlertListComponent,
    CapAlertComponent,
    CapActionbarComponent,
    CapNavItemComponent,
    NavMenuComponent,
    HeaderComponent,
    UserPickerComponent,
    ExcludePipe,
  ],
  exports: [
    TranslateModule,
    CapAlertListComponent,
    CapAlertComponent,
    CapActionbarComponent,
    UserPickerComponent,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    ReactiveFormsModule,
    NgbModule,
    CapNavItemComponent,
    NavMenuComponent,
    HeaderComponent,
    ExcludePipe
  ],
})
export class CoreModule {
  static forRoot(): ModuleWithProviders<CoreModuleForRoot> {
    return {
      ngModule: CoreModuleForRoot,
      // we had to define a separate module rather than inlining the providers here,
      // because BedagStyleModule.forRoot() does not make its providers accessible by means other than an import,
      // which can not be specified when using the ModuleWithProviders syntax ...
    };
  }
}

@NgModule({
  imports: [
    CoreModule,
    HttpClientModule,
    OAuthModule.forRoot(),
    LogModule.forRoot(),
    Ng2CompleterModule,
    GbApiModule.forRoot(apiConfigFactory),
    StammdatenApiModule.forRoot(apiConfigFactory),
    BaseApiModule.forRoot(apiConfigFactory),
    AvApiModule.forRoot(apiConfigFactory),
    AuftragApiModule.forRoot(apiConfigFactory),
    GwrApiModule.forRoot(apiConfigFactory),
    PersonstammApiModule.forRoot(apiConfigFactory),
    GrunbuchmeldungApiModule.forRoot(apiConfigFactory),
    MapsApipModule.forRoot(apiConfigFactory),
    OerebApiModule.forRoot(apiConfigFactory),
    AmtlichebewerungApiModule.forRoot(apiConfigFactory),
    BatchAuftragApiModule.forRoot(apiConfigFactory),
  ],
  exports: [
    CoreModule,
  ],
  providers: [
    OAuthModule,
    AppAuszugCookieService,
    AppConfigService,
    AppCustomConfigService,
    AppGbgemeindeCompleterService,
    AppMenuService,
    AppPermissionService,
    BreadcrumbService,
    BodenbedeckungViewmodelService,
    CapAlertService,
    CapExceptionHandler,
    CapExceptionMapper,
    CookieService,
    EigentumViewmodelService,
    FremdsystemErrorExceptionHandler,
    FremdsystemNeskoErrorExceptionHandler,
    ElasticSearchExceptionHandler,
    IcpPrintExceptionHandler,
    GbPrintService,
    GrundpfandrechtViewmodelService,
    LogAndAlertExceptionHandler,
    MenuTreeService,
    NoPermissionExceptionHandler,
    ResolverValidationExceptionHandler,
    RouteHistoryService,
    SearchResultValidationExceptionHandler,
    StandardrechtViewmodelService,
    RangregelungViewmodelService,
    TimepointService,
    SuchparameterService,
    TooManyRequestsExceptionHandler,
    UnauthorizedExceptionHandler,
    MizenExceptionHandler,
    {provide: FormatConfigService, useClass: AppFormatConfigService},
    {provide: LocationStrategy, useClass: PathLocationStrategy},
    {provide: ErrorHandler, useClass: CapExceptionHandler},
    {provide: HTTP_INTERCEPTORS, useClass: ClientSessionIdHttpInterceptor, multi: true},
    {provide: HTTP_INTERCEPTORS, useClass: SearchResponseInterceptor, multi: true},
    {provide: HTTP_INTERCEPTORS, useClass: ErrorHandlingHttpInterceptor, multi: true},
    {provide: HTTP_INTERCEPTORS, useClass: HistoryModeHttpInterceptor, multi: true, deps: [AppConfigService]},
    {provide: CapiLoginService, useClass: CapiLoginService},
  ],
})

export class CoreModuleForRoot {
}

